export type BsiTransferMode = 'local' | 'interbank';

export type BsiTransferModeInput = {
  iban?: string | null;
  card_number?: string | null;
  account_number?: string | null;
  bank?: { name?: string | null } | null;
};

function normalizeIban(value?: string | null): string {
  return (value ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

/** فرمت موردنیاز بانک صادرات: IRXX-XXXX-XXXX-XXXX-XXXX-XXXX-XX */
export function formatIranIbanDashed(value?: string | null): string {
  let compact = normalizeIban(value);
  if (!compact) return '';
  if (!compact.startsWith('IR') && /^\d{24}$/.test(compact)) {
    compact = `IR${compact}`;
  }
  if (!compact.startsWith('IR')) return compact;
  const digits = compact.slice(2).replace(/\D/g, '').slice(0, 24);
  const parts = [
    digits.slice(0, 2),
    digits.slice(2, 6),
    digits.slice(6, 10),
    digits.slice(10, 14),
    digits.slice(14, 18),
    digits.slice(18, 22),
    digits.slice(22, 24),
  ].filter(Boolean);
  if (parts.length === 0) return 'IR';
  return `IR${parts[0]}${parts.length > 1 ? `-${parts.slice(1).join('-')}` : ''}`;
}

export function isCompleteIranIban(value?: string | null): boolean {
  let compact = normalizeIban(value);
  if (/^\d{24}$/.test(compact)) compact = `IR${compact}`;
  return /^IR\d{24}$/.test(compact);
}

/** کد بانک صادرات در شبا: 019 */
export function ibanBankCodeIsSaderat(iban?: string | null): boolean {
  const normalized = normalizeIban(iban);
  const match = /^IR\d{2}(\d{3})/.exec(normalized);
  return match?.[1] === '019';
}

/**
 * استخراج شماره حساب صادرات از شبا IR…019…
 * ساختار: IR + ۲ رقم کنترل + ۰۱۹ + ۱۹ رقم BBAN
 * صفرهای پدینگ اول حذف می‌شوند ولی طول نهایی کمتر از ۱۳ نمی‌شود
 * (مثلاً 0112437073005 نه 112437073005).
 */
export function accountNumberFromSaderatIban(iban?: string | null): string | null {
  const normalized = normalizeIban(iban);
  if (!ibanBankCodeIsSaderat(normalized)) return null;
  const digits = normalized.slice(4);
  if (digits.length < 6) return null;
  let accountPart = digits.slice(3);
  while (accountPart.startsWith('0') && accountPart.length > 13) {
    accountPart = accountPart.slice(1);
  }
  return accountPart !== '' ? accountPart : null;
}

function bankNameIsSaderat(name?: string | null): boolean {
  const compact = (name ?? '').replace(/\s+/g, '');
  if (compact.includes('توسعهصادرات')) return false;
  return compact.includes('صادرات');
}

export function isSaderatBankAccount(input: BsiTransferModeInput): boolean {
  if (ibanBankCodeIsSaderat(input.iban)) return true;
  const card = (input.card_number ?? '').replace(/\D/g, '');
  if (card.startsWith('603769')) return true;
  if (bankNameIsSaderat(input.bank?.name)) return true;
  return false;
}

export function resolveBsiTransferMode(input: BsiTransferModeInput): BsiTransferMode {
  if (isSaderatBankAccount(input)) return 'local';
  const iban = normalizeIban(input.iban);
  if (iban.startsWith('IR') && iban.length >= 7) return 'interbank';
  return 'local';
}

export function bsiTransferModeLabel(mode: BsiTransferMode): string {
  return mode === 'local' ? 'انتقال داخل بانک صادرات' : 'انتقال بین‌بانکی (ساتنا/پایا)';
}

/** برای انتقال داخل صادرات: شماره حساب. برای بین‌بانکی: شبا. هرگز کارت. */
export function resolveBsiDestinationAccount(
  input: BsiTransferModeInput & { suggested_bsi_mode?: BsiTransferMode | null },
): string | null {
  const mode = input.suggested_bsi_mode ?? resolveBsiTransferMode(input);

  if (mode === 'interbank') {
    const iban = normalizeIban(input.iban);
    return iban.startsWith('IR') ? formatIranIbanDashed(iban) : null;
  }

  const account = (input.account_number ?? '').trim();
  if (account !== '') {
    return account.replace(/\s+/g, '');
  }

  const fromIban = accountNumberFromSaderatIban(input.iban);
  if (fromIban) return fromIban;

  return null;
}

export function bsiLocalDestinationMissingAccount(input: BsiTransferModeInput): boolean {
  return resolveBsiTransferMode(input) === 'local' && !resolveBsiDestinationAccount({ ...input, suggested_bsi_mode: 'local' });
}
